import React, { useState } from "react";

const MessageSection = ({
  openDropdown,
  toggleDropdown,
  whatsappTemplates,
  formData,
  errors,
  handleTemplateSelect,
  selectedTemplateData,
  isMediaTemplate,
  variableValues,
  handleVariableChange,
  buttonValues,
  handleButtonChange,
  mediaFile,
  handleMediaUpload,
  templateMap
}) => {
  const [templateFilter, setTemplateFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Filter templates based on category and search query
  const filteredTemplates = whatsappTemplates.filter(tpl => {
    const matchesCategory = templateFilter === "all" || tpl.category === templateFilter;
    const matchesSearch = tpl.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         tpl.language.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get unique categories for filter
  const categories = [...new Set(whatsappTemplates.map(tpl => tpl.category))];

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div 
        className="card-header bg-white d-flex justify-content-between align-items-center py-3" 
        onClick={toggleDropdown} 
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex align-items-center">
          <div 
            className="step-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{ width: "32px", height: "32px", fontSize: "14px", fontWeight: "600" }}
          >
            2
          </div>
          <div>
            <h5 className="mb-0">Create your Message</h5>
            <small className="text-muted">
              {formData.messageTemplate 
                ? `Selected: ${formData.messageTemplate}` 
                : "Choose a WhatsApp template"
              }
            </small>
          </div>
        </div>
        <span className="text-muted fs-5">
          {openDropdown ? "▲" : "▼"}
        </span>
      </div>
      
      {openDropdown && (
        <div className="card-body pt-3">
          <div className="row">
            <div className="col-lg-4 mb-4">
              <div className="sticky-top" style={{ top: "20px", zIndex: 10 }}>
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-white">
                    <h6 className="mb-0">Select Template</h6>
                  </div>
                  <div className="card-body">
                    {/* Search input */}
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    {/* Category filter */}
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">Filter by Category</label>
                      <select
                        className="form-select"
                        value={templateFilter}
                        onChange={(e) => setTemplateFilter(e.target.value)}
                      >
                        <option value="all">All Categories</option>
                        {categories.map(category => (
                          <option key={category} value={category}>
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Template selection */}
                    <div className={`form-select-wrapper ${errors.messageTemplate ? "is-invalid" : ""}`}>
                      <select
                        className="form-select"
                        value={formData.messageTemplate}
                        onChange={(e) => handleTemplateSelect(e.target.value)}
                      >
                        <option value="">Choose a template...</option>
                        {filteredTemplates.map((tpl) => (
                          <option key={tpl.id} value={tpl.name}>
                            {tpl.name} ({tpl.category}) - {tpl.language}
                          </option>
                        ))}
                      </select>
                      {errors.messageTemplate && (
                        <div className="invalid-feedback d-block">{errors.messageTemplate}</div>
                      )}
                    </div>
                    
                    {/* Template info */}
                    {selectedTemplateData && (
                      <div className="mt-3 p-3 bg-light rounded">
                        <small className="d-block fw-medium">Template Details</small>
                        <small className="d-block">Category: {selectedTemplateData.category}</small>
                        <small className="d-block">Language: {selectedTemplateData.language}</small>
                        <small className="d-block">Status: 
                          <span className={`badge ms-1 ${selectedTemplateData.status === "APPROVED" ? "bg-success" : "bg-warning"}`}>
                            {selectedTemplateData.status}
                          </span>
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="col-lg-8">
              {selectedTemplateData ? (
                <div className="template-preview">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Template Preview</h6>
                    <small className="text-muted">ID: {selectedTemplateData.id}</small>
                  </div>
                  
                  {/* WhatsApp message preview */}
                  <div className="whatsapp-preview-container mb-4">
                    <div className="whatsapp-preview bg-light rounded-3 p-3">
                      {/* Header section */}
                      {selectedTemplateData.components.find(c => c.type === "HEADER") && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Header</small>
                          <div className="p-3 bg-white rounded-2 border">
                            {isMediaTemplate ? (
                              <div className="text-center py-2">
                                <i className="bi bi-file-earmark-image fs-2 text-muted"></i>
                                <div className="mt-1 small">[Media Content]</div>
                                {mediaFile && (
                                  <div className="mt-2 small text-primary">
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                    {mediaFile.name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              selectedTemplateData.components.find(c => c.type === "HEADER").text
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Body section with variables */}
                      {selectedTemplateData.components.find(c => c.type === "BODY") && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Message Body</small>
                          <div className="p-3 bg-white rounded-2 border position-relative">
                            {selectedTemplateData.components.find(c => c.type === "BODY").text.split(/({{\d+}})/).map((part, i) => {
                              if (part.match(/{{\d+}}/)) {
                                const varIndex = part.match(/\d+/)[0];
                                const varValue = variableValues[`var${varIndex}`] || `[Variable ${varIndex}]`;
                                return (
                                  <span key={i} className="variable-placeholder text-primary">
                                    {varValue}
                                  </span>
                                );
                              }
                              return part;
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Footer section */}
                      {selectedTemplateData.components.find(c => c.type === "FOOTER") && (
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">Footer</small>
                          <div className="p-3 bg-white rounded-2 border">
                            {selectedTemplateData.components.find(c => c.type === "FOOTER").text}
                          </div>
                        </div>
                      )}
                      
                      {/* Buttons section */}
                      {selectedTemplateData.allButtons.length > 0 && (
                        <div className="mt-3">
                          <small className="text-muted d-block mb-1">Buttons</small>
                          <div className="d-flex flex-wrap gap-2">
                            {selectedTemplateData.allButtons.map((btn, i) => (
                              <div key={i} className="btn btn-outline-primary btn-sm position-relative">
                                {btn.type === "PHONE_NUMBER" && <i className="bi bi-telephone me-1"></i>}
                                {btn.type === "URL" && <i className="bi bi-link me-1"></i>}
                                {btn.type === "QUICK_REPLY" && <i className="bi bi-reply me-1"></i>}
                                {btn.type === "COPY_CODE" && <i className="bi bi-clipboard me-1"></i>}
                                {btn.text}
                                {(btn.type === "PHONE_NUMBER" || btn.type === "COPY_CODE") && (
                                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-info">
                                    <i className="bi bi-gear"></i>
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Variables section */}
                  {selectedTemplateData?.variables?.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm">
                      <div className="card-header bg-white">
                        <h6 className="mb-0">Template Variables</h6>
                        <small className="text-muted">Customize the message content</small>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          {selectedTemplateData.variables.map((variable, index) => (
                            <div className="col-md-6 mb-3" key={index}>
                              <label className="form-label small fw-semibold">
                                Variable {index + 1} <span className="text-muted">({variable})</span>
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                value={variableValues[`var${index + 1}`] || ""}
                                onChange={(e) => handleVariableChange(e, `var${index + 1}`)}
                                placeholder={`Enter value for ${variable}`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Dynamic buttons configuration */}
                  {selectedTemplateData?.dynamicButtons?.length > 0 && (
                    <div className="card mb-4 border-0 shadow-sm">
                      <div className="card-header bg-white">
                        <h6 className="mb-0">Button Configuration</h6>
                        <small className="text-muted">Set up interactive buttons</small>
                      </div>
                      <div className="card-body">
                        {selectedTemplateData.dynamicButtons.map((button, index) => (
                          <div className="mb-4 p-3 bg-light rounded-2" key={index}>
                            <h6 className="d-flex align-items-center">
                              {button.type === "PHONE_NUMBER" && 
                                <i className="bi bi-telephone text-primary me-2"></i>}
                              {button.type === "COPY_CODE" && 
                                <i className="bi bi-clipboard text-primary me-2"></i>}
                              {button.type.replace("_", " ")} Button
                            </h6>
                            
                            {button.type === "PHONE_NUMBER" && (
                              <div className="row">
                                <div className="col-md-6 mb-2">
                                  <label className="form-label small fw-semibold">Button Text</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={buttonValues[`phone_btn_${index}_text`] || button.text || ""}
                                    onChange={(e) => handleButtonChange(e, `phone_btn_${index}_text`)}
                                    placeholder="Button text"
                                  />
                                </div>
                                <div className="col-md-6 mb-2">
                                  <label className="form-label small fw-semibold">Phone Number</label>
                                  <div className="input-group">
                                    <span className="input-group-text">+</span>
                                    <input
                                      type="tel"
                                      className="form-control"
                                      value={buttonValues[`phone_btn_${index}_number`] || ""}
                                      onChange={(e) => handleButtonChange(e, `phone_btn_${index}_number`)}
                                      placeholder="1234567890"
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {button.type === "COPY_CODE" && (
                              <div className="row">
                                <div className="col-md-6 mb-2">
                                  <label className="form-label small fw-semibold">Button Text</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={buttonValues[`copy_btn_${index}_text`] || button.text || ""}
                                    onChange={(e) => handleButtonChange(e, `copy_btn_${index}_text`)}
                                    placeholder="Button text"
                                  />
                                </div>
                                <div className="col-md-6 mb-2">
                                  <label className="form-label small fw-semibold">Code Value</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={buttonValues[`copy_btn_${index}_code`] || ""}
                                    onChange={(e) => handleButtonChange(e, `copy_btn_${index}_code`)}
                                    placeholder="Code to copy"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Media upload section */}
                  {isMediaTemplate && (
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-white">
                        <h6 className="mb-0">Media Upload</h6>
                        <small className="text-muted">
                          Upload your {selectedTemplateData.components
                            .find(c => c.type === "HEADER" && ["IMAGE", "VIDEO", "DOCUMENT"].includes(c.format))
                            ?.format?.toLowerCase()} file
                        </small>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <div className="file-upload-area border rounded-2 p-4 text-center bg-light">
                            <input
                              type="file"
                              className="file-input d-none"
                              id="mediaFile"
                              accept={
                                selectedTemplateData.components.find(
                                  c => c.type === "HEADER" && c.format === "IMAGE"
                                )
                                  ? "image/*"
                                  : selectedTemplateData.components.find(
                                      c => c.type === "HEADER" && c.format === "VIDEO"
                                    )
                                    ? "video/*"
                                    : "*/*"
                              }
                              onChange={handleMediaUpload}
                            />
                            <label htmlFor="mediaFile" className="file-upload-label cursor-pointer">
                              <div className="mb-2">
                                <i className="bi bi-cloud-upload display-4 text-muted"></i>
                              </div>
                              <h6 className="mb-1">Click to upload media</h6>
                              <p className="text-muted small mb-0">
                                Supported formats: {
                                  selectedTemplateData.components.find(
                                    c => c.type === "HEADER" && c.format === "IMAGE"
                                  )
                                    ? "JPG, PNG, GIF"
                                    : selectedTemplateData.components.find(
                                        c => c.type === "HEADER" && c.format === "VIDEO"
                                      )
                                      ? "MP4, MOV"
                                      : "PDF, DOC, XLS"
                                }
                              </p>
                            </label>
                          </div>
                          
                          {mediaFile && (
                            <div className="mt-3 p-3 bg-white rounded-2 border d-flex align-items-center">
                              <i className="bi bi-file-earmark-check text-success me-2"></i>
                              <div className="flex-grow-1">
                                <div className="fw-medium">{mediaFile.name}</div>
                                <small className="text-muted">
                                  {(mediaFile.size / 1024).toFixed(1)} KB
                                </small>
                              </div>
                              <button 
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleMediaUpload({ target: { files: [] } })}
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-chat-square-text display-4 d-block mb-3"></i>
                  <h5>Select a template to get started</h5>
                  <p>Choose a WhatsApp template from the sidebar to preview and customize your message</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageSection;